import { Box, Flex } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { RefObject } from 'react';
import type { FormikProps } from 'formik';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle } from '@kula-ai/icons-core';
import { Text } from '@kula-ai/components';
// kula-ai ships a `Drawer`, but its footer is a bare `footer?: ReactNode`
// slot: no primary/secondary CTA contract, no `isCtaLoading`/`loadingText`,
// and no submit handler. This form needs all three, and has to look identical
// to the HRIS employee form (`EmployeeFormDrawer`) it opens beside — so the
// internal drawer stays until kula's grows a CTA-with-loading footer. That
// looks like it belongs upstream in `@kula-ai/components`.
// Imported as a file, not a barrel, to keep the ATS tree out of this folder's
// import graph.
import { Drawer } from '@/components/ats/drawer/Drawer';
import { FormBuilder } from '@/components/formBuilder/FormBuilder';
import { useShowToast } from '@/hooks';
import { WORKDAY_POSITION_API } from './api';
import { usePositionReferenceOptions } from './usePositionReferenceOptions';
import { buildPositionFormConfig } from './positionFormConfig';
import { useJobProfileDefault } from './useJobProfileDefault';
import {
  buildInitialPositionValues,
  getCreatePositionError,
  hasEnvelopeErrors,
  toCreatePositionPayload,
} from './positionForm';
import type { CreatePositionFormValues, CreatePositionResult } from './types';

/**
 * Writes the resolved Job profile default into the form once it is known.
 *
 * Applied to the field rather than to `initialValues`: FormBuilder runs with
 * `enableReinitialize`, so changing initial values once the match lands would
 * reset anything already filled in. Never overwrites a real choice.
 *
 * Its own component because it has to run *inside* the drawer body: the body
 * mounts after the drawer's own effects, so a `formRef` read from the parent is
 * still null on every open after the first, leaving the field blank.
 */
const SeededDefaults = ({
  formRef,
  jobProfileId,
  jobTitle,
}: {
  formRef: RefObject<FormikProps<CreatePositionFormValues> | null>;
  jobProfileId?: string;
  jobTitle?: string;
}) => {
  useEffect(() => {
    const form = formRef.current;
    if (!jobProfileId || !form || form.values?.job_profile_id) return;
    form.setFieldValue('job_profile_id', jobProfileId);
  }, [formRef, jobProfileId]);

  useEffect(() => {
    const form = formRef.current;
    if (!jobTitle || !form || form.values?.job_posting_title) return;
    form.setFieldValue('job_posting_title', jobTitle);
  }, [formRef, jobTitle]);

  return null;
};

export interface CreatePositionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  applicationId?: string | number;
  /** Prefills the editable Job title field. */
  jobTitle?: string | null;
  /** Kula job, used to default the Job profile by name. */
  jobId?: string | number;
  /** Profile name when the application already carries it. */
  jobProfileName?: string | null;
  candidateName?: string;
  /** Called with the `syncing` position the create call returned. */
  onCreated?: (_position: CreatePositionResult) => void;
}

export const CreatePositionDrawer = ({
  isOpen,
  onClose,
  applicationId,
  jobTitle,
  jobId,
  jobProfileName,
  candidateName = '',
  onCreated,
}: CreatePositionDrawerProps) => {
  const toast = useShowToast();
  const formRef = useRef<FormikProps<CreatePositionFormValues>>(null);
  const { loadOptionsFor, hasTruncatedList, isEmptyList } =
    usePositionReferenceOptions({ isOpen });

  const { jobProfile: jobProfileDefault, jobName } = useJobProfileDefault({
    jobId,
    jobProfileName,
    enabled: isOpen,
    hasJobTitle: !!jobTitle,
  });

  // A candidates-table row carries no `ats_job`, so the host cannot pass a
  // title and the field opened blank. Fall back to the job this drawer already
  // fetches for the profile default — same cache entry, no extra request.
  const resolvedJobTitle = jobTitle || jobName;

  const formConfig = useMemo(
    () =>
      buildPositionFormConfig({
        loadOptionsFor,
        isEmptyList,
        jobProfileDefault,
      }),
    [loadOptionsFor, isEmptyList, jobProfileDefault]
  );

  // Built from the prop alone, never from the async fallback. FormBuilder runs
  // Formik with `enableReinitialize`, so a title that lands after first paint
  // would reinitialise the form and wipe everything already filled in —
  // including a Team pick that costs a 1,750-row list to make. Anything
  // resolved late is written imperatively by `SeededDefaults` instead.
  const initialValues = useMemo(
    () => buildInitialPositionValues(jobTitle),
    [jobTitle]
  );

  // Destructured: the useMutation result is not referentially stable, so the
  // whole object cannot go into a dependency array.
  const { mutateAsync: createPosition, isPending: isSubmitting } = useMutation({
    mutationFn: (values: CreatePositionFormValues) =>
      WORKDAY_POSITION_API.createPosition({
        ats_application_id: applicationId as string | number,
        ...(toCreatePositionPayload(values) as CreatePositionFormValues),
      }),
  });

  const handleCreate = useCallback(
    async (values: CreatePositionFormValues) => {
      try {
        const response = await createPosition(values);
        // A 2xx can still carry `errors` — the Kombo envelope reports failures
        // both ways, so a 200 alone is not success.
        if (hasEnvelopeErrors(response)) {
          toast({
            title: getCreatePositionError(response),
            status: 'error',
          });
          return;
        }
        toast({ title: 'Position created in Workday', status: 'success' });
        onCreated?.(response?.data || {});
        onClose();
      } catch (error) {
        // Toast both outcomes, as every other HRIS call does. The drawer stays
        // open on failure so the recruiter can correct and resubmit.
        toast({ title: getCreatePositionError(error), status: 'error' });
      }
    },
    [createPosition, toast, onCreated, onClose]
  );

  const handleSubmitForm = useCallback(async () => {
    await formRef.current?.submitForm();

    // `data-field-name` is FormFieldContainer's scroll anchor; a select renders
    // nothing findable by `name`.
    const [firstError] = Object.keys(formRef.current?.errors || {});
    if (firstError) {
      document
        .querySelector(`[data-field-name="${firstError}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  if (!isOpen) return null;

  return (
    <Drawer
      drawerActions={{ isOpen, onClose }}
      headerText={`Create Workday position${
        candidateName ? ` for ${candidateName}` : ''
      }`}
      headerDescription="A position must exist in Workday before the candidate can be hired into it."
      secondaryCtaLabel="Cancel"
      secondaryCtaClick={onClose}
      ctaLabel="Create position"
      loadingText="Creating"
      isCtaLoading={isSubmitting}
      onDrawerSubmit={handleSubmitForm}
      ctaProps={{ isDisabled: isSubmitting, minW: '170px' }}
      hasFooterDivider={true}
      headerTextProps={{
        borderBottom: '1px solid',
        borderBottomColor: 'border-color',
      }}
      drawerBodyProps={{ overflowY: 'auto' }}
    >
      <Flex w="100%" h="100%" flex={1} direction="column" overflow="hidden">
        {/* DrawerBody is p="0" — the employee form gets its inset from the
            CollapseSection wrappers its schema builds; this form is flat. */}
        <Box flex={1} overflowY="auto" px="16px" pb="16px">
          {hasTruncatedList && (
            <Flex
              mt="16px"
              gap="6px"
              p="12px"
              w="100%"
              alignItems="flex-start"
              background="pastel-6-light"
              borderRadius="6px"
            >
              <Box pt="2px">
                <AlertCircle size={16} className="kula-icon" />
              </Box>
              <Text>
                Workday returned more options than we can show. Search for the
                exact name.
              </Text>
            </Flex>
          )}

          <FormBuilder
            config={formConfig}
            formRef={formRef}
            initialValues={initialValues}
            onSubmit={handleCreate}
          />
          <SeededDefaults
            formRef={formRef}
            jobProfileId={jobProfileDefault?.id}
            jobTitle={resolvedJobTitle}
          />
        </Box>
      </Flex>
    </Drawer>
  );
};

export default CreatePositionDrawer;
